import api from './api';

export type AdminStudentRecord = {
  id: number;
  name: string;
  email: string;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  role_id?: number | null;
  role_slug?: string | null;
  role_name?: string | null;
  role?: {
    id?: number | null;
    name?: string | null;
    slug?: string | null;
  } | null;
};

export type PaginatedResponse<T> = {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export type FetchAdminStudentsParams = {
  search?: string;
  page?: number;
  perPage?: number;
};

const toInt = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export async function fetchAdminStudents(params: FetchAdminStudentsParams = {}) {
  const { search, page, perPage } = params;
  const query: Record<string, unknown> = {};

  if (search) {
    query.search = search;
  }

  if (typeof page === 'number' && page > 0) {
    query.page = page;
  }

  if (typeof perPage === 'number' && perPage > 0) {
    query.per_page = perPage;
  }

  const { data } = await api.get('admin/students', { params: query });

  const fallbackCount = Array.isArray(data) ? data.length : 0;
  const fallbackMeta = {
    current_page: page ?? 1,
    last_page: page ?? 1,
    per_page: Number.isFinite(perPage) && perPage ? perPage : fallbackCount || 15,
    total: Number.isFinite(perPage) && perPage ? perPage : fallbackCount,
  };

  if (data && typeof data === 'object') {
    const payload = data as Record<string, any>;
    if (Array.isArray(payload.data)) {
      const metaSource =
        (payload.meta && typeof payload.meta === 'object'
          ? payload.meta
          : payload) as Record<string, any>;
      const normalizedMeta = {
        current_page: toInt(
          metaSource?.current_page ?? metaSource?.currentPage,
          fallbackMeta.current_page,
        ),
        last_page: toInt(
          metaSource?.last_page ?? metaSource?.lastPage,
          fallbackMeta.last_page,
        ),
        per_page: toInt(
          metaSource?.per_page ?? metaSource?.perPage,
          fallbackMeta.per_page,
        ),
        total: toInt(metaSource?.total, fallbackMeta.total),
      };

      return {
        data: payload.data,
        meta: normalizedMeta,
      };
    }
  }

  if (Array.isArray(data)) {
    return {
      data,
      meta: {
        current_page: page ?? 1,
        last_page: page ?? 1,
        per_page: perPage ?? data.length,
        total: data.length,
      },
    };
  }

  return {
    data: [],
    meta: fallbackMeta,
  };
}

export type UpdateAdminStudentPayload = {
  name: string;
  email: string;
  role: 'student' | 'admin';
};

export async function updateAdminStudent(id: number, payload: UpdateAdminStudentPayload) {
  const { data } = await api.put(`admin/students/${id}`, payload);
  return data as AdminStudentRecord;
}

export async function sendAdminStudentReset(id: number) {
  const { data } = await api.post(`admin/students/${id}/reset-password`);
  return data as { status: string };
}
